import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { observer } from "mobx-react";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
    GroupType,
    KeyFieldType,
    SelectedTargetFields,
    TargetFieldOptions,
} from "../types";
import {
    arrowStyles,
    fieldStyles,
    getDefaultValue,
    selectCustomStyle,
} from "../helpers/componentUtils";
import useHoverAction from "../hooks/useHoverAction";
import DeleteButton from "../components/DeleteButton";
import CustomOption from "../components/CustomOption";

type KeyFieldProps = KeyFieldType & {
    sourceFieldOptions: { label: string; value: string }[];
    sourcesWithCoreTargets: string[];
    getUpdatedTargetFields: () => {
        targetFieldOptions: TargetFieldOptions;
        selectedTargetFields: SelectedTargetFields;
    };
    updateKeyField: (
        id: string,
        key: string,
        value: string,
        targetFieldId?: string,
    ) => void;
    removeKeyField: () => void;
};

const KeyField: React.FC<KeyFieldProps> = observer(
    ({
        id,
        sourceFieldName,
        sourceFieldOptions,
        sourcesWithCoreTargets,
        updateKeyField,
        removeKeyField,
        getUpdatedTargetFields,
    }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const { showAction, handlers } = useHoverAction(containerRef);

        const { targetFieldOptions, selectedTargetFields } =
            getUpdatedTargetFields();

        const defaultValue = useMemo(
            () =>
                getDefaultValue({
                    id,
                    selectedTargetFields,
                    targetFieldOptions,
                }),
            [id, selectedTargetFields, targetFieldOptions],
        );

        useEffect(() => {
            // If a field is selected, and it is changed to no longer be indexed, clear the selection
            if (defaultValue?.isIndexed === false) {
                updateKeyField(id, "targetFieldName", "");
            }
        }, [defaultValue, id, updateKeyField]);

        // AUT-7541 - Only indexed extended metadata fields should be shown for Key Fields
        const optsWithOnlyIndexedExtendedMetadata =
            targetFieldOptions.grouped.map((group: GroupType) => {
                if (group.label === "Extended Metadata") {
                    return {
                        ...group,
                        options: group.options?.filter(
                            (op) => op.isIndexed || op.onClick,
                        ), // Keep all indexed fields and custom options for adding/managing fields
                    };
                }
                return group;
            });

        // If the source has a core target, we can only select extended metadata as a "Key Field for Target"
        const showOnlyExtendedMetadata =
            sourcesWithCoreTargets.includes(sourceFieldName);

        // Hide core fields but show extended fields and no selection options
        const updatedTargetFieldOptions = showOnlyExtendedMetadata
            ? optsWithOnlyIndexedExtendedMetadata.filter(
                  (group) => group.label !== "AMD Core Fields",
              )
            : optsWithOnlyIndexedExtendedMetadata;

        return (
            <Box
                ref={containerRef}
                {...handlers}
                sx={{
                    ...fieldStyles,
                }}
            >
                <AcxSelect
                    customStyle={selectCustomStyle({ flex: 15 })}
                    fullWidth
                    id={`source-field-${id}`}
                    options={sourceFieldOptions}
                    placeholder="Select"
                    defaultValue={sourceFieldOptions.find(
                        (e) => sourceFieldName && e.value === sourceFieldName,
                    )}
                    onChange={(e) =>
                        updateKeyField(id, "sourceFieldName", e.value)
                    }
                />
                <ArrowForwardOutlinedIcon sx={arrowStyles} />
                <AcxSelect
                    optionComponent={CustomOption}
                    customStyle={selectCustomStyle({ flex: 15 })}
                    id={`target-field-${id}`}
                    options={updatedTargetFieldOptions}
                    placeholder="Select"
                    defaultValue={defaultValue}
                    onChange={(e) =>
                        // The no selection option has an empty value
                        updateKeyField(
                            id,
                            "targetFieldName",
                            e.value === "" ? "" : e.label,
                            e.id,
                        )
                    }
                />
                <Box
                    sx={{ flex: 1, justifyContent: "center", display: "flex" }}
                >
                    <DeleteButton
                        showDelete={showAction}
                        onDelete={removeKeyField}
                    />
                </Box>
            </Box>
        );
    },
);

export default KeyField;
